import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Loader2, Download, FileText, Music, Calendar as CalIcon, FolderOpen, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import CaseStageTracker from "../components/CaseStageTracker";

function formatBytes(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

export default function Portal() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portal/me")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const downloadFile = async (file) => {
    try {
      const resp = await api.get(`/portal/files/${file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
      </div>
    );
  }

  const { profile, files, bookings } = data || {};

  return (
    <div className="bg-black min-h-screen" data-testid="portal-page">
      <div className="container-pl py-16 lg:py-20">
        {/* Header */}
        <div className="flex items-baseline justify-between flex-wrap gap-4 mb-12">
          <div>
            <p className="overline mb-3">Client Portal</p>
            <h1 className="display-serif text-4xl sm:text-5xl text-zinc-50">
              Welcome back, {user.name?.split(" ")[0]}.
            </h1>
          </div>
          <div className="text-right">
            <p className="text-xs text-zinc-400 font-mono tracking-[0.15em] uppercase">Account</p>
            <p className="text-sm text-zinc-200">{user.email}</p>
          </div>
        </div>

        {!profile ? (
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-12 text-center" data-testid="no-profile">
            <FolderOpen className="h-10 w-10 text-zinc-600 mx-auto mb-6" strokeWidth={1.25} />
            <h2 className="display-serif text-2xl text-zinc-50 mb-3">
              Your portal will be populated once your case begins.
            </h2>
            <p className="text-sm text-zinc-400 max-w-md mx-auto mb-8">
              Schedule a consultation to get started. After your intake, your case will appear here
              with progress updates, documents, and appointment history.
            </p>
            <Link to="/booking">
              <Button className="rounded-sm bg-blue-700 hover:bg-blue-600 h-11 px-7" data-testid="portal-schedule-btn">
                Schedule Consultation
                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
              </Button>
            </Link>

            {bookings && bookings.length > 0 && (
              <div className="mt-12 text-left max-w-2xl mx-auto">
                <p className="overline mb-4">Your appointments</p>
                <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800">
                  {bookings.map((b) => (
                    <div key={b.id} className="p-4 flex justify-between items-center" data-testid={`portal-booking-${b.id}`}>
                      <div>
                        <p className="text-sm font-medium text-zinc-50 capitalize">
                          {b.appointment_type === "phone" ? "Phone Call" : "Consultation"}
                        </p>
                        <p className="text-xs text-zinc-400">{b.date} · {b.time_slot}</p>
                      </div>
                      <Badge variant="outline" className="rounded-sm text-xs">{b.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-10">
            {/* Case stage tracker */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-8" data-testid="case-stage-card">
              <div className="flex justify-between items-baseline mb-6">
                <p className="overline">Case progress</p>
                <Badge variant="outline" className="rounded-sm text-xs">
                  {profile.status}
                </Badge>
              </div>
              <CaseStageTracker stage={profile.case_stage || "intake"} />
            </div>

            {/* Case info + Notes */}
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-8" data-testid="case-info-card">
                <p className="overline mb-4">Case details</p>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">Name</p>
                    <p className="text-zinc-50 mt-1">{profile.full_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">Case #</p>
                    <p className="font-mono text-xs text-zinc-200 mt-1">{profile.case_number || "—"}</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 bg-zinc-950 border border-zinc-800 rounded-sm p-8" data-testid="case-notes-card">
                <p className="overline mb-4">Case notes & updates</p>
                {profile.notes ? (
                  <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{profile.notes}</p>
                ) : (
                  <p className="text-sm text-zinc-500">No notes yet. Your paralegal will post updates here.</p>
                )}
              </div>
            </div>

            {/* Documents */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm">
              <div className="p-6 border-b border-zinc-800">
                <p className="overline">Your documents</p>
              </div>
              {files.length === 0 ? (
                <div className="p-10 text-center text-sm text-zinc-500" data-testid="portal-no-files">
                  No documents uploaded yet.
                </div>
              ) : (
                <div className="divide-y divide-zinc-800" data-testid="portal-files-list">
                  {files.map((f) => (
                    <div key={f.id} className="p-5 flex items-center justify-between gap-4" data-testid={`portal-file-${f.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        {f.category === "audio" ? (
                          <Music className="h-4 w-4 text-amber-400 flex-shrink-0" strokeWidth={1.5} />
                        ) : (
                          <FileText className="h-4 w-4 text-amber-400 flex-shrink-0" strokeWidth={1.5} />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-zinc-50 truncate">{f.original_filename}</p>
                          <p className="text-xs text-zinc-400">{formatBytes(f.size)} · {new Date(f.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <Button onClick={() => downloadFile(f)} variant="ghost" size="sm" className="rounded-sm" data-testid={`portal-download-${f.id}`}>
                        <Download className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Appointments */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm">
              <div className="p-6 border-b border-zinc-800 flex justify-between items-center">
                <p className="overline">Appointment history</p>
                <Link to="/booking" className="text-xs text-amber-400 link-underline">Schedule another</Link>
              </div>
              {bookings.length === 0 ? (
                <div className="p-10 text-center text-sm text-zinc-500" data-testid="portal-no-bookings">
                  No appointments yet.
                </div>
              ) : (
                <div className="divide-y divide-zinc-800" data-testid="portal-bookings-list">
                  {bookings.map((b) => (
                    <div key={b.id} className="p-5 flex items-center justify-between gap-4" data-testid={`portal-booking-${b.id}`}>
                      <div className="flex items-center gap-3">
                        <CalIcon className="h-4 w-4 text-amber-400 flex-shrink-0" strokeWidth={1.5} />
                        <div>
                          <p className="text-sm font-medium text-zinc-50 capitalize">
                            {b.appointment_type === "phone" ? "Phone Call" : "Consultation"}
                          </p>
                          <p className="text-xs text-zinc-400">{b.date} · {b.time_slot}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="rounded-sm text-xs">{b.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
